import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Plus, Calendar, ArrowRight, Trash2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { fmtDateMed, fmtDayMonth, fmtRange } from "../lib/dates";
import { toast } from "sonner";
import { format, addDays } from "date-fns";

const Rosters = () => {
  const navigate = useNavigate();
  const [rosters, setRosters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    start_date: null,
    end_date: null,
    duration: null // weeks
  });

  useEffect(() => {
    fetchRosters();
  }, []);

  const fetchRosters = async () => {
    try {
      const response = await axios.get(`${API}/rosters`, { withCredentials: true });
      setRosters(response.data);
    } catch (error) {
      console.error("Failed to fetch rosters:", error);
      toast.error("Failed to load rosters");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.start_date || !formData.end_date) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const response = await axios.post(`${API}/rosters`, {
        name: formData.name,
        start_date: format(formData.start_date, "yyyy-MM-dd"),
        end_date: format(formData.end_date, "yyyy-MM-dd")
      }, { withCredentials: true });
      toast.success("Roster created");
      setDialogOpen(false);
      resetForm();
      navigate(`/rosters/${response.data.roster_id}`);
    } catch (error) {
      console.error("Failed to create roster:", error);
      toast.error("Failed to create roster");
    }
  };

  const handleDelete = async (rosterId) => {
    if (!window.confirm("Are you sure you want to delete this roster?")) return;
    
    try {
      await axios.delete(`${API}/rosters/${rosterId}`, { withCredentials: true });
      toast.success("Roster deleted");
      fetchRosters();
    } catch (error) {
      console.error("Failed to delete roster:", error);
      toast.error("Failed to delete roster");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      start_date: null,
      end_date: null,
      duration: null
    });
  };

  const handleStartDateSelect = (date) => {
    setFormData(prev => {
      const newState = { ...prev, start_date: date };
      if (prev.duration) {
        newState.end_date = addDays(date, prev.duration * 7 - 1);
      } else if (prev.end_date && prev.end_date < date) {
        newState.end_date = null;
      }
      return newState;
    });
  };

  const handleDurationSelect = (weeks) => {
    setFormData(prev => {
      const newState = { ...prev, duration: weeks };
      if (prev.start_date) {
        newState.end_date = addDays(prev.start_date, weeks * 7 - 1);
      }
      return newState;
    });
  };

  const setQuickDateRange = (days) => {
    const start = new Date();
    const end = addDays(start, days - 1);
    setFormData(prev => ({
      ...prev,
      start_date: start,
      end_date: end,
      duration: days / 7
    }));
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Rosters</h1>
            <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
              Create and manage AI-generated work rosters
            </p>
          </div>
          <Button
            onClick={() => {
              resetForm();
              setDialogOpen(true);
            }}
            className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded"
            data-testid="create-roster-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Roster
          </Button>
        </div>

        {/* Roster List */}
        {rosters.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-12 text-center">
            <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-6 h-6 text-zinc-400 dark:text-zinc-500" />
            </div>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">No rosters yet</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-4">Create your first roster to get started with AI scheduling</p>
            <Button
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
              className="bg-[#002FA7] hover:bg-[#00227A] text-white font-medium rounded"
              data-testid="empty-create-roster"
            >
              Create Roster
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 dark:border-zinc-800">
            {rosters.map((roster) => (
              <div key={roster.roster_id} className="bg-white dark:bg-zinc-900 p-6 flex flex-col">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg tracking-tight font-medium text-zinc-900 dark:text-zinc-100">
                      {roster.name}
                    </h3>
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">
                      {fmtRange(roster.start_date, roster.end_date)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(roster.roster_id);
                    }}
                    className="text-zinc-400 dark:text-zinc-500 hover:text-red-600"
                    data-testid={`delete-roster-${roster.roster_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-2 flex-1">
                  <div className="flex items-center justify-between py-2 border-b border-zinc-100 dark:border-zinc-800">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Status</span>
                    <span className={`
                      text-xs font-medium px-2 py-0.5 rounded capitalize
                      ${roster.status === 'published' 
                        ? 'bg-emerald-50 text-emerald-700' 
                        : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400'
                      }
                    `}>
                      {roster.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Entries</span>
                    <span className="text-sm tabular-nums font-medium text-zinc-900 dark:text-zinc-100">
                      {roster.entries?.length || 0}
                    </span>
                  </div>
                </div>
                
                <Button
                  variant="outline"
                  className="mt-4 w-full justify-between"
                  onClick={() => navigate(`/rosters/${roster.roster_id}`)}
                  data-testid={`view-roster-${roster.roster_id}`}
                >
                  View Roster
                  <ArrowRight className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Create Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl tracking-tight font-bold">
                Create New Roster
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                  Roster Name *
                </Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Week 1 January, Q1 Schedule"
                  className="mt-1"
                  data-testid="roster-name-input"
                />
              </div>

              {/* Roster Length */}
              <div>
                <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400 mb-2 block">
                  Roster Length *
                </Label>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {[
                    { weeks: 1, label: "1 Week", days: "7 days" },
                    { weeks: 2, label: "2 Weeks", days: "14 days" },
                    { weeks: 3, label: "3 Weeks", days: "21 days" },
                    { weeks: 4, label: "4 Weeks", days: "28 days" },
                    { weeks: 5, label: "5 Weeks", days: "35 days" },
                    { weeks: 6, label: "6 Weeks", days: "42 days" },
                  ].map((opt) => (
                    <button
                      key={opt.weeks}
                      type="button"
                      onClick={() => handleDurationSelect(opt.weeks)}
                      className={`
                        p-3 border rounded text-left transition-colors
                        ${formData.duration === opt.weeks
                          ? 'border-[#002FA7] bg-[#002FA7]/5'
                          : 'border-zinc-200 dark:border-zinc-800 hover:border-zinc-300'
                        }
                      `}
                      data-testid={`duration-${opt.weeks}w`}
                    >
                      <p className={`text-sm font-medium ${formData.duration === opt.weeks ? 'text-[#002FA7]' : 'text-zinc-900 dark:text-zinc-100'}`}>
                        {opt.label}
                      </p>
                      <p className="text-xs text-zinc-500 dark:text-zinc-400">{opt.days}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Start Date & Computed End Date */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Start Date *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-1 justify-start text-left font-normal"
                        data-testid="roster-start-date"
                      >
                        {formData.start_date ? fmtDateMed(formData.start_date) : "Pick date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarPicker
                        mode="single"
                        selected={formData.start_date}
                        onSelect={handleStartDateSelect}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    End Date {formData.duration ? "(auto)" : "*"}
                  </Label>
                  {formData.duration ? (
                    <div className="mt-1 px-3 py-2 border border-zinc-200 dark:border-zinc-800 rounded bg-zinc-50 dark:bg-zinc-800/50 text-sm text-zinc-700 dark:text-zinc-300">
                      {formData.end_date ? fmtDateMed(formData.end_date) : "Select start date"}
                    </div>
                  ) : (
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full mt-1 justify-start text-left font-normal"
                          data-testid="roster-end-date"
                        >
                          {formData.end_date ? fmtDateMed(formData.end_date) : "Pick date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <CalendarPicker
                          mode="single"
                          selected={formData.end_date}
                          onSelect={(date) => setFormData({ ...formData, end_date: date })}
                          disabled={(date) => formData.start_date && date < formData.start_date}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  )}
                </div>
              </div>

              {/* Summary */}
              {formData.start_date && formData.end_date && (
                <div className="bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-800 p-3 rounded">
                  <div className="flex items-center justify-between">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Summary</span>
                    {formData.duration && (
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, duration: null }))}
                        className="text-xs text-zinc-400 dark:text-zinc-500 hover:text-zinc-600 dark:text-zinc-400 underline"
                        data-testid="clear-duration"
                      >
                        Custom dates
                      </button>
                    )}
                  </div>
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mt-1">
                    {fmtDayMonth(formData.start_date)} — {fmtDateMed(formData.end_date)}
                  </p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    {Math.ceil((formData.end_date - formData.start_date) / (1000 * 60 * 60 * 24)) + 1} days
                    {formData.duration ? ` (${formData.duration} week${formData.duration > 1 ? 's' : ''})` : ''}
                  </p>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  data-testid="cancel-roster-btn"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                  data-testid="save-roster-btn"
                >
                  Create Roster
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Rosters;
